/**
 * Die Vorladungen — was auf diesem Gerät aufpoppen muss, egal auf welchem
 * Screen die Person gerade steht.
 *
 * Der Zeugenstand: jemand erklärt, getilgt zu haben, und dieses Mitglied ist
 * als Zeuge benannt. Erst die Aussage hier macht die Tilgung wirksam.
 * Dazu Battle-Herausforderungen, die auf Annahme warten.
 *
 * Eine Vorladung deckt den ganzen Bildschirm ab. Deshalb gilt hier eine
 * Regel: **jede Vorladung wird an Ort und Stelle erledigt.** Wer von hier aus
 * woandershin geschickt wird, läuft gegen die eigene Wache — sie liegt ja
 * weiter darüber. Und für alles gibt es „Später": eine Vorladung darf zur
 * Not warten, sie darf niemanden einsperren.
 */
import { useCallback, useEffect, useState } from 'react'
import { api, ApiFehler } from '../api/client.js'
import { formatEinheiten } from '../api/format.js'
import type { Vorladungen as VorladungenDaten } from '../api/typen.js'
import { Amtsknopf, Fehlermeldung } from './Bausteine.js'

const TAKT_MS = 10_000
/** Wie lange ein „Später" gilt, bevor die Kammer erneut ruft. */
const VERTAGT_MS = 5 * 60 * 1000

export function VorladungsWache() {
  const [daten, setDaten] = useState<VorladungenDaten | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  const [laeuft, setLaeuft] = useState(false)
  /** Vorladungen, die diese Person vertagt hat: Schlüssel → Ablaufzeitpunkt. */
  const [vertagt, setVertagt] = useState<Record<string, number>>({})

  const lade = useCallback(() => {
    api
      .get<VorladungenDaten>('/api/vorladungen')
      .then(setDaten)
      .catch(() => {
        // Kein Netz, abgelaufene Sitzung — die Wache bleibt still.
      })
  }, [])

  useEffect(() => {
    lade()
    const takt = setInterval(lade, TAKT_MS)
    return () => clearInterval(takt)
  }, [lade])

  const vertage = (schluessel: string): void => {
    setVertagt((v) => ({ ...v, [schluessel]: Date.now() + VERTAGT_MS }))
  }
  const istVertagt = (schluessel: string): boolean => (vertagt[schluessel] ?? 0) > Date.now()

  const zeugenstand = daten?.zeugenstand.find((v) => !istVertagt(`zeuge-${v.antragId}`))
  const herausforderung = daten?.herausforderungen.find(
    (h) => !istVertagt(`battle-${h.battleId}`),
  )

  async function rufe(pfad: string, body?: unknown): Promise<boolean> {
    setLaeuft(true)
    setFehler(null)
    try {
      await api.post(pfad, body)
      lade()
      return true
    } catch (f) {
      setFehler(f instanceof ApiFehler ? f.message : 'Der Antrag ist nicht durchgekommen.')
      return false
    } finally {
      setLaeuft(false)
    }
  }

  // Der Zeugenstand hat Vorrang — eine Vorladung nach der anderen.
  if (zeugenstand) {
    return (
      <Wache titel="Vorladung · Zeugenstand" ueberschrift="Die Kammer ruft dich auf">
        <p className="mt-3 text-sm text-akte/85">
          <strong className="text-lampe">{zeugenstand.vonName}</strong> erklärt,{' '}
          {formatEinheiten(zeugenstand.amountUnits)} getilgt zu haben
          {zeugenstand.alkoholfrei ? ' — alkoholfrei' : ''}. Hast du es gesehen?
        </p>
        <p className="protokoll mt-2 normal-case">{zeugenstand.reasonText}</p>
        <div className="mt-4 grid grid-cols-1 gap-2">
          <Amtsknopf
            disabled={laeuft}
            onClick={() =>
              void rufe(`/api/tilgungsantraege/${zeugenstand.antragId}/aussage`, {
                bestaetigt: true,
              })
            }
          >
            Ich bezeuge es
          </Amtsknopf>
          <Amtsknopf
            variante="leder"
            disabled={laeuft}
            onClick={() =>
              void rufe(`/api/tilgungsantraege/${zeugenstand.antragId}/aussage`, {
                bestaetigt: false,
              })
            }
          >
            Ich habe nichts gesehen
          </Amtsknopf>
        </div>
        <p className="protokoll mt-3 normal-case">
          Erst deine Aussage macht die Tilgung wirksam.
        </p>
        <Fehlermeldung text={fehler} />
        <Spaeter onClick={() => vertage(`zeuge-${zeugenstand.antragId}`)} />
      </Wache>
    )
  }

  if (herausforderung) {
    return (
      <Wache titel="Vorladung · §11 Schnapsbattle" ueberschrift="Du wirst gefordert">
        <p className="mt-3 text-sm text-akte/85">
          <strong className="text-lampe">{herausforderung.vonName}</strong> fordert dich zum
          Schnapsbattle. Nimmst du an?
        </p>
        <div className="mt-4 grid grid-cols-2 gap-2">
          <Amtsknopf
            disabled={laeuft}
            onClick={() =>
              void rufe(`/api/battles/${herausforderung.battleId}/annehmen`).then((gut) => {
                // Angenommen — jetzt wird gewürfelt, und zwar dort.
                if (gut) window.location.assign(`/termine/${herausforderung.eventId}`)
              })
            }
          >
            Annehmen
          </Amtsknopf>
          <Amtsknopf
            variante="leder"
            disabled={laeuft}
            onClick={() => void rufe(`/api/battles/${herausforderung.battleId}/ablehnen`)}
          >
            Ablehnen
          </Amtsknopf>
        </div>
        <p className="protokoll mt-3 normal-case">
          Wer annimmt, würfelt selbst — der Verlierer dreht am Schnapsrad.
        </p>
        <Fehlermeldung text={fehler} />
        <Spaeter onClick={() => vertage(`battle-${herausforderung.battleId}`)} />
      </Wache>
    )
  }

  return null
}

function Wache({
  titel,
  ueberschrift,
  children,
}: {
  titel: string
  ueberschrift: string
  children: React.ReactNode
}) {
  return (
    <div
      role="alertdialog"
      aria-modal="true"
      aria-label={ueberschrift}
      className="fixed inset-0 z-50 flex items-center justify-center bg-saal/85 px-5 backdrop-blur-sm"
    >
      <div className="w-full max-w-96 border border-messing/60 bg-bank p-5 text-center">
        <p className="protokoll">{titel}</p>
        <h2 className="mt-3 text-xl">{ueberschrift}</h2>
        {children}
      </div>
    </div>
  )
}

/** Der Notausgang: eine Vorladung darf warten, aber niemanden einsperren. */
function Spaeter({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="protokoll mt-4 normal-case underline decoration-messing/40 underline-offset-4 hover:text-messing"
    >
      Später — in fünf Minuten erneut vorladen
    </button>
  )
}
